import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, 
  Paper, CircularProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AnalysisRequest, AnalysisResult } from '../types';
import { downloadAnalysisAsXLSX } from '../utils/excelUtils';
import AnalysisCharts from './AnalysisCharts';

// Update the interface to include onReset
interface AnalysisProps {
  filename: string;
  sheets: string[];
  columnMappings: Record<string, any>;
  onAnalysisComplete: (result: AnalysisResult) => void;
  onBack: () => void;
  onReset: () => void; // Add this new prop
}

const Analysis: React.FC<AnalysisProps> = ({
  filename,
  sheets,
  columnMappings,
  onAnalysisComplete,
  onBack,
  onReset,
}) => {
  const [yearRange, setYearRange] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [reportName, setReportName] = useState('');
  const [nexusStates, setNexusStates] = useState<any[]>([]);

  useEffect(() => {
    // Get current year for default year range
    const currentYear = new Date().getFullYear();
    setYearRange(`${currentYear - 1}-${currentYear}`);
  }, []);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError(null);

    try {
      const request: AnalysisRequest = {
        filename,
        yearRange,
        sheets,
        columnMappings,
      };

      const response = await axios.post<AnalysisResult>('/api/analysis', request);
      const result = response.data;
      
      setResult(result);
      
      // Extract states where nexus was met
      const filteredStates = result.analysis_data.filter((state: any) => state['Met Nexus'] === 'Yes');
      
      // Filter to have only one entry per state (the first year it met nexus)
      const stateMap: Record<string, any> = {};
      
      filteredStates.forEach((state: any) => {
        const stateName = state.State;
        const year = parseInt(state.Year);
        
        if (!stateMap[stateName] || year < stateMap[stateName].Year) {
          stateMap[stateName] = state;
        }
      });
      
      // Convert back to array
      setNexusStates(Object.values(stateMap));
      
      onAnalysisComplete(result);
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Failed to analyze data. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    downloadAnalysisAsXLSX(result.analysis_data, reportName);
  };

  return (
    <Box>
      {!result ? (
        <Box>
          <Typography variant="h2" gutterBottom>
            Analysis Configuration
          </Typography>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Year Range</Typography>
            
            <Typography variant="body2" color="textSecondary" paragraph>
              Enter the year range to analyze (e.g., "2023-2024" for analyzing data from 2023 and 2024).
            </Typography>
            
            <TextField
              label="Year Range"
              value={yearRange}
              onChange={(e) => setYearRange(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="YYYY-YYYY"
              helperText="Enter start and end years separated by a hyphen"
            />
          </Paper>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              variant="outlined" 
              onClick={onBack}
              startIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleAnalyze}
              disabled={analyzing || !yearRange}
            >
              {analyzing ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Analyzing...
                </>
              ) : (
                'Run Analysis'
              )}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h2">
              Analysis Results
            </Typography>
          </Box>

          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Download Analysis
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField
                label="Report Name (Optional)"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                sx={{ flexGrow: 1, mr: 2 }}
                placeholder="Enter custom filename or leave blank"
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
              >
                Download XLSX
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h5" gutterBottom>
              Summary
            </Typography>
            
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Year</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Transactions</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{result.grand_totals.year1.year}</TableCell>
                    <TableCell>{result.grand_totals.year1?.count?.toLocaleString() || 0}</TableCell>
                    <TableCell>
                      ${(result.grand_totals.year1?.amount || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{result.grand_totals.year2.year}</TableCell>
                    <TableCell>{result.grand_totals.year2?.count?.toLocaleString() || 0}</TableCell>
                    <TableCell>
                      ${(result.grand_totals.year2?.amount || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {result && <AnalysisCharts result={result} />}

            <Typography variant="h5" gutterBottom>
              States Where Nexus Was Met
            </Typography>
            
            {nexusStates.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>State</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Threshold Type Met</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Crossed On</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Sales</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Total Orders</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nexusStates.map((state, index) => {
                      const thresholdMet = [];
                      if (state["Threshold - Total Pct"] >= 100)
                        thresholdMet.push("Sales");
                      if (state["Threshold - Count Pct"] >= 100)
                        thresholdMet.push("Transactions");

                      const totalAtCrossing = state["Period Type"]
                        ?.toLowerCase()
                        .includes("rolling")
                        ? state["total_at_crossing"]
                        : state["Customer Data Total"];
                      const countAtCrossing = state["Period Type"]
                        ?.toLowerCase()
                        .includes("rolling")
                        ? state["count_at_crossing"]
                        : state["Customer Data Count"];

                      return (
                        <TableRow key={index}>
                          <TableCell>{state.State}</TableCell>
                          <TableCell>{thresholdMet.join(" & ")}</TableCell>
                          <TableCell>{state["Crossed on"]}</TableCell>
                          <TableCell>
                            ${totalAtCrossing.toLocaleString(undefined, {
                              minimumFractionDigits: 2
                            })}
                          </TableCell>
                          <TableCell>{countAtCrossing.toLocaleString()}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" align="center" sx={{ p: 2 }}>
                No states met nexus thresholds.
              </Typography>
            )}
          </Paper>
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={onReset}
            >
              Start New Analysis
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Analysis;